import type { Constant } from '@pbt/pbt-ui-components'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import { RxType } from '~/api/graphql/generated/types'
import { getCommonPackagingLabel } from '~/components/dashboard/admin/catalog/inventory/inventoryUtils'
import { PrescriptionType } from '~/constants/prescription'
import i18n from '~/locales/i18n'
import type {
  Prescription,
  PrescriptionDrugInfo,
  PrescriptionWithTypeField,
} from '~/types/entities/prescriptions'
import { getManualInputSelectValue } from '~/utils'
import { getPrescriptionQuantityUnits } from '~/utils/prescriptionUtils'

const REFILL_SEPARATOR = ' | '
const GENERIC_UNIT_TYPES = ['unit', 'item', 'other'] as const

export type VetDietConstants = {
  InventoryProductSizeUnit: Constant[]
  PackageType: Constant[]
  ProductForm: Constant[]
}

export interface QuantityAndRefillsOptions {
  includeRefills?: boolean
  parentRx?: Prescription
  preferAdministrationUnit?: boolean
  prescriptionProductSizeUnit?: Constant[]
  productSizeUnit?: Constant[]
  quantityAndUnitOnly?: boolean
  usePerRefillQuantity?: boolean
  vetDietConstants?: VetDietConstants
}

export interface QtyRequestedDisplay {
  primary: string
  secondary?: string | null
}

function trimOrEmpty(
  v: string | number | boolean | bigint | null | undefined,
): string {
  if (typeof v === 'string') return v.trim()
  if (v == null) return ''
  return String(v)
}

const hasVetDietPackagingFields = (
  drugInfo: PrescriptionDrugInfo | undefined,
): boolean =>
  (drugInfo?.perPackageAmount ?? drugInfo?.perPackageUnitsId) != null

const getFormString = (
  drugInfo: PrescriptionDrugInfo | undefined,
  ProductForm: Constant[],
): string => {
  if (!drugInfo) return ''
  const formId =
    (drugInfo as { form?: { id?: string } }).form?.id ?? drugInfo.formId ?? null
  return trimOrEmpty(
    getManualInputSelectValue(ProductForm, formId, drugInfo.customForm),
  )
}

/**
 * Builds the vet diet unit label (form + flavor + packaging).
 * Matches VetDietInfo / SwapProductReactiveForm display.
 */
export const getVetDietUnitLabel = (
  drugInfo: PrescriptionDrugInfo | undefined,
  ProductForm: Constant[],
  PackageType: Constant[],
  InventoryProductSizeUnit: Constant[],
): string => {
  if (!drugInfo) return ''

  const formStr = getFormString(drugInfo, ProductForm)

  // Don't pass saleUnitOfMeasure when it duplicates perPackageUnits (getCommonPackagingLabel appends both; same value => "Can(s) Can(s)").
  const perPackageUnitsResolved = LanguageUtils.getConstantTranslatedName(
    drugInfo.perPackageUnitsId,
    InventoryProductSizeUnit,
  )
  const saleTrimmed = trimOrEmpty(drugInfo.saleUnitOfMeasure)
  const resolvedTrimmed = trimOrEmpty(perPackageUnitsResolved)
  const omitSaleUnit =
    saleTrimmed !== '' &&
    resolvedTrimmed !== '' &&
    saleTrimmed.toLowerCase() === resolvedTrimmed.toLowerCase()

  const packageUnitFields = {
    perPackageUnitsId: drugInfo.perPackageUnitsId,
    perPackageAmount: drugInfo.perPackageAmount,
    ...(omitSaleUnit ? {} : { saleUnitOfMeasure: drugInfo.saleUnitOfMeasure }),
  }

  const packaging = getCommonPackagingLabel(
    InventoryProductSizeUnit,
    PackageType,
  )(packageUnitFields)

  return LanguageUtils.getSentence([formStr, drugInfo.flavor, packaging]).trim()
}

function getQuantityValue(
  rx: Prescription,
  parentRx?: Prescription,
  usePerRefill?: boolean,
): number {
  const perRefill = usePerRefill
    ? (rx.quantityPerRefill ?? parentRx?.quantityPerRefill)
    : undefined

  return perRefill ?? rx.totalQuantity ?? parentRx?.totalQuantity ?? 0
}

/** Merges child rx with parent (e.g. refill row); child wins when present, else parent. */
const mergeRxWithParent = (
  rx: Prescription,
  parentRx: Prescription,
): Prescription => ({
  ...parentRx,
  ...rx,
  totalQuantityUnitId: rx.totalQuantityUnitId ?? parentRx.totalQuantityUnitId,
  totalQuantityCustomUnit:
    rx.totalQuantityCustomUnit ?? parentRx.totalQuantityCustomUnit,
  quantityPerRefillUnitId:
    rx.quantityPerRefillUnitId ?? parentRx.quantityPerRefillUnitId,
  quantityPerRefillCustomUnit:
    rx.quantityPerRefillCustomUnit ?? parentRx.quantityPerRefillCustomUnit,
  variation: rx.variation ?? parentRx.variation,
  globalVariation: rx.globalVariation ?? parentRx.globalVariation,
  rxType: rx.rxType ?? parentRx.rxType,
})

const getDrugInfo = (
  rx: Prescription,
  parentRx?: Prescription,
): PrescriptionDrugInfo | undefined => {
  const childHasPackaging = hasVetDietPackagingFields(rx.drugInfo)
  return parentRx?.drugInfo && !childHasPackaging
    ? (parentRx.drugInfo ?? undefined)
    : (rx.drugInfo ?? parentRx?.drugInfo ?? undefined)
}

const shouldShowVetDietLabel = (
  drugInfo: PrescriptionDrugInfo | undefined,
  rxTypeToCheck: string | RxType | undefined,
  vetDietConstants: VetDietConstants | undefined,
): boolean =>
  Boolean(drugInfo) &&
  Boolean(vetDietConstants) &&
  (rxTypeToCheck === RxType.VetDiet || hasVetDietPackagingFields(drugInfo))

export const isGenericUnitType = (unitStr: string): boolean =>
  Boolean(
    unitStr &&
    GENERIC_UNIT_TYPES.includes(
      unitStr.toLowerCase() as (typeof GENERIC_UNIT_TYPES)[number],
    ),
  )

interface ResolveUnitOptions {
  drugInfo: PrescriptionDrugInfo | undefined
  quantityAndUnitOnly?: boolean
  rxTypeToCheck: string | RxType | undefined
  vetDietConstants: VetDietConstants | undefined
}

function resolveUnitForDisplay(
  unitStr: string,
  isGeneric: boolean,
  options: ResolveUnitOptions,
): string {
  const { drugInfo, quantityAndUnitOnly, rxTypeToCheck, vetDietConstants } =
    options

  if (
    !quantityAndUnitOnly &&
    shouldShowVetDietLabel(drugInfo, rxTypeToCheck, vetDietConstants) &&
    vetDietConstants
  ) {
    const vetDietLabel = getVetDietUnitLabel(
      drugInfo,
      vetDietConstants.ProductForm,
      vetDietConstants.PackageType,
      vetDietConstants.InventoryProductSizeUnit,
    )
    if (vetDietLabel) return vetDietLabel
  }

  if ((!unitStr || isGeneric) && vetDietConstants?.ProductForm) {
    const formStr = trimOrEmpty(
      getFormString(drugInfo, vetDietConstants.ProductForm),
    )
    if (formStr) return formStr
  }

  return unitStr
}

const buildRefillText = (quantityNumbRefills: number | undefined): string =>
  (quantityNumbRefills ?? 0) > 0
    ? `${REFILL_SEPARATOR}${i18n.t('Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS', {
        number: quantityNumbRefills,
      })}`
    : `${REFILL_SEPARATOR}${i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.NO_REFILLS')}`

function isChewyRx(r: PrescriptionWithTypeField): boolean {
  const t = r.prescriptionType ?? r.type
  return t === PrescriptionType.CHEWY
}

const getBackendResolvedUnit = (rx: Prescription): string | null => {
  const trimmed = trimOrEmpty(
    (rx as Prescription & { totalQuantityUnitResolved?: string })
      .totalQuantityUnitResolved,
  )
  return trimmed || null
}

export const getQtyRequested = (
  rx: Prescription,
  options: QuantityAndRefillsOptions = {},
): QtyRequestedDisplay => {
  const {
    includeRefills = true,
    parentRx,
    preferAdministrationUnit = false,
    prescriptionProductSizeUnit = [],
    productSizeUnit = [],
    quantityAndUnitOnly = false,
    usePerRefillQuantity = false,
    vetDietConstants,
  } = options

  const quantity = getQuantityValue(rx, parentRx, usePerRefillQuantity)
  const rxForUnit = parentRx ? mergeRxWithParent(rx, parentRx) : rx

  const refillText = includeRefills
    ? buildRefillText(
        rx.quantityNumbRefills ?? parentRx?.quantityNumbRefills ?? undefined,
      )
    : ''

  const isChewy = isChewyRx(rx) || (parentRx != null && isChewyRx(parentRx))
  if (isChewy) {
    const { administrationQuantityUnit } = getPrescriptionQuantityUnits(
      rxForUnit,
      {
        PrescriptionProductSizeUnit: prescriptionProductSizeUnit,
        ProductSizeUnit: productSizeUnit,
      },
    )
    const r = rxForUnit as {
      quantityPerRefillUnit?: { name?: string }
      totalQuantityUnit?: { name?: string }
    }
    const unitNameFromRx = usePerRefillQuantity
      ? r.quantityPerRefillUnit?.name
      : r.totalQuantityUnit?.name
    const unit =
      trimOrEmpty(administrationQuantityUnit) ||
      trimOrEmpty(unitNameFromRx) ||
      i18n.t('Common:UNIT')

    return {
      primary: `${quantity} ${unit}${refillText}`,
      secondary: null,
    }
  }

  const drugInfo = getDrugInfo(rx, parentRx)

  const effectiveRx: Prescription = usePerRefillQuantity
    ? {
        ...rxForUnit,
        totalQuantityUnitId:
          rxForUnit.quantityPerRefillUnitId ?? rxForUnit.totalQuantityUnitId,
        totalQuantityCustomUnit:
          rxForUnit.quantityPerRefillCustomUnit ??
          rxForUnit.totalQuantityCustomUnit,
      }
    : rxForUnit

  const { quantityUnit: standardUnit, administrationQuantityUnit } =
    getPrescriptionQuantityUnits(effectiveRx, {
      PrescriptionProductSizeUnit: prescriptionProductSizeUnit,
      ProductSizeUnit: productSizeUnit,
    })

  const backendResolvedUnit = getBackendResolvedUnit(effectiveRx)
  const hasExplicitAdministrationUnit = Boolean(
    effectiveRx.administrationQuantityUnitId ||
    effectiveRx.administrationQuantityCustomUnit,
  )
  const baseUnit =
    preferAdministrationUnit &&
    hasExplicitAdministrationUnit &&
    trimOrEmpty(administrationQuantityUnit)
      ? trimOrEmpty(administrationQuantityUnit)
      : (backendResolvedUnit ?? trimOrEmpty(standardUnit))

  const resolvedUnit = resolveUnitForDisplay(
    baseUnit,
    isGenericUnitType(baseUnit),
    {
      drugInfo,
      quantityAndUnitOnly,
      rxTypeToCheck: parentRx?.rxType ?? effectiveRx.rxType ?? undefined,
      vetDietConstants,
    },
  )

  const quantityUnit = quantity + (resolvedUnit ? ` ${resolvedUnit}` : '')
  return { primary: quantityUnit + refillText, secondary: null }
}
